import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { fetchAaveUserData, NETWORKS } from '../services/aave';
import { fetchVenusUserData } from '../services/venus';
import { GET_USER_WALLETS } from '../graphql/queries/userWallets';
import { ADD_USER_WALLET, REMOVE_USER_WALLET } from '../graphql/mutations/userWallets';
import { parseFloatFromString } from '../utils/formatters';

const formatNumber = (val) => {
  if (!val) return '0';
  try {
    const n = Number(val);
    if (!isFinite(n)) return val;
    if (n === 0) return '0';
    if (Math.abs(n) < 0.01) return n.toExponential(2);
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  } catch (_e) {
    return val;
  }
};

const formatUSD = (val) => {
  const n = parseFloatFromString(val);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const HealthBadge = ({ health }) => {
  const n = Number(health) / 1e18; // healthFactor is ray-like 1e18
  const isCap = !isFinite(n) || n >= 10;
  const color = isCap ? 'bg-green-600' : n === 0 ? 'bg-gray-500' : n < 1.05 ? 'bg-red-600' : n < 1.5 ? 'bg-yellow-600' : 'bg-green-600';
  const text = isCap ? '10+' : n === 0 ? '—' : n.toFixed(2);
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded text-white ${color}`}>{text}</span>
  );
};

const LendingCheckPage = () => {
  const [wallet, setWallet] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [walletStates, setWalletStates] = useState([]); // per-wallet sections for stored wallets
  const [singleResult, setSingleResult] = useState(null); // ad-hoc single check state
  const [progressMsg, setProgressMsg] = useState('');
  const [, setTimeTick] = useState(Date.now()); // triggers periodic re-render for timeAgo
  const [removeModal, setRemoveModal] = useState(null); // { address, label }
  const [selectedWallet, setSelectedWallet] = useState(null); // filter to a single stored wallet
  const [selectedProtocols, setSelectedProtocols] = useState({}); // per-wallet selected protocol id
  const { data: walletsData, refetch: refetchWallets } = useQuery(GET_USER_WALLETS);
  const [addWallet] = useMutation(ADD_USER_WALLET, { onCompleted: () => refetchWallets() });
  const [removeWallet] = useMutation(REMOVE_USER_WALLET, { onCompleted: () => refetchWallets() });

  const storedWallets = useMemo(() => walletsData?.userWallets || [], [walletsData]);

  // Simple localStorage cache per wallet
  const CACHE_KEY = 'aaveWalletCache:v1';
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day
  const readCache = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch (_e) { return {}; }
  };
  const writeCache = (obj) => {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch (_e) {}
  };
  const getCacheEntry = (address) => {
    const key = String(address).toLowerCase();
    const cache = readCache();
    return cache[key];
  };
  const setCacheEntry = (address, results) => {
    const key = String(address).toLowerCase();
    const cache = readCache();
    cache[key] = { updatedAt: Date.now(), results };
    writeCache(cache);
  };
  const clearCacheEntry = (address) => {
    const key = String(address).toLowerCase();
    const cache = readCache();
    delete cache[key];
    writeCache(cache);
  };
  const isFresh = (entry) => entry && typeof entry.updatedAt === 'number' && (Date.now() - entry.updatedAt) < CACHE_TTL_MS;
  const timeAgo = (ts) => {
    if (!ts) return '';
    const sec = Math.floor((Date.now() - ts) / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.floor(hr / 24);
    return `${d}d ago`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setData([]);
    setSingleResult(null);
    const trimmed = wallet.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      setError('Enter a valid EVM address');
      return;
    }
    setLoading(true);
    try {
      setSingleResult({ address: trimmed, loading: true, error: '', results: [] });
      const labels = Object.values(NETWORKS).map((n) => n.label).join(', ');
      setProgressMsg(`Checking across: ${labels}`);
      const [aave, venus] = await Promise.all([
        fetchAaveUserData(trimmed),
        fetchVenusUserData(trimmed),
      ]);
      const results = [...aave, ...venus];
      setData(results);
      setSingleResult({ address: trimmed, loading: false, error: '', results });
      setProgressMsg('');
    } catch (err) {
      const msg = err?.message || 'Failed to fetch data';
      setError(msg);
      setSingleResult({ address: trimmed, loading: false, error: msg, results: [] });
      setProgressMsg('');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStored = async (e) => {
    e.preventDefault();
    const trimmed = wallet.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      setError('Enter a valid EVM address');
      return;
    }
    await addWallet({ variables: { address: trimmed } });
    // Fetch immediately for the new wallet and cache
    setWalletStates((prev) => [{ address: trimmed, label: null, loading: true, error: '', results: [], updatedAt: null }, ...prev]);
    try {
      const [aave, venus] = await Promise.all([
        fetchAaveUserData(trimmed),
        fetchVenusUserData(trimmed),
      ]);
      const results = [...aave, ...venus];
      setCacheEntry(trimmed, results);
      setWalletStates((prev) => prev.map((w) => w.address.toLowerCase() === trimmed.toLowerCase() ? { ...w, loading: false, error: '', results, updatedAt: Date.now() } : w));
    } catch (err) {
      setWalletStates((prev) => prev.map((w) => w.address.toLowerCase() === trimmed.toLowerCase() ? { ...w, loading: false, error: err?.message || 'Failed to fetch data' } : w));
    }
    setWallet('');
  };

  const handleRemoveStored = async (address) => {
    await removeWallet({ variables: { address } });
    if (selectedWallet && selectedWallet.toLowerCase() === address.toLowerCase()) {
      setSelectedWallet(null);
    }
  };

  const handleRefreshStored = async (force = true) => {
    if (!storedWallets.length) return;
    setError('');
    // Prepare states; respect cache when not forcing
    const initial = storedWallets.map((w) => {
      const entry = getCacheEntry(w.address);
      if (!force && isFresh(entry)) {
        return { address: w.address, label: w.label, loading: false, error: '', results: entry.results, updatedAt: entry.updatedAt };
      }
      if (force) clearCacheEntry(w.address);
      return { address: w.address, label: w.label, loading: true, error: '', results: [], updatedAt: null };
    });
    setWalletStates(initial);
    setProgressMsg('Preparing checks…');
    try {
      const tasks = storedWallets.map((w, idx) => {
        const entry = getCacheEntry(w.address);
        if (!force && isFresh(entry)) {
          return Promise.resolve({ idx, address: w.address, fromCache: true, results: entry.results });
        }
        return Promise.all([fetchAaveUserData(w.address), fetchVenusUserData(w.address)])
          .then(([aave, venus]) => ({ idx, address: w.address, results: [...aave, ...venus] }))
          .catch((err) => ({ idx, address: w.address, error: err?.message || 'Failed to fetch data' }));
      });
      const settled = await Promise.all(tasks.map(async (p) => {
        const r = await p;
        setProgressMsg(`Checked ${r.address.slice(0,6)}…${r.address.slice(-4)}`);
        return r;
      }));
      setWalletStates((prev) => {
        const next = [...prev];
        for (const item of settled) {
          if (item.error) {
            next[item.idx] = { ...next[item.idx], loading: false, error: item.error, results: [] };
          } else {
            const preservedUpdatedAt = next[item.idx]?.updatedAt || null;
            const updatedAt = item.fromCache ? preservedUpdatedAt : Date.now();
            next[item.idx] = { ...next[item.idx], loading: false, error: '', results: item.results, updatedAt };
            if (!item.fromCache) setCacheEntry(next[item.idx].address, item.results);
          }
        }
        return next;
      });
      setProgressMsg('');
    } catch (err) {
      setError(err?.message || 'Failed to fetch data');
      setProgressMsg('');
    }
  };

  useEffect(() => {
    if (storedWallets.length > 0) {
      // initialize from cache or fetch missing/stale
      handleRefreshStored(false);
    } else {
      setWalletStates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedWallets.length]);

  // Periodic tick to update timeAgo outputs
  useEffect(() => {
    const id = setInterval(() => setTimeTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const displayed = data
    .filter((net) => {
      if (net?.error) return true;
      const us = net?.userSummary;
      if (!us) return false;
      const usdTotal = (Number(us.totalCollateralUSD || '0') || 0) + (Number(us.totalBorrowsUSD || '0') || 0);
      if (usdTotal > 0) return true;
      // Fallback: consider raw balances in case USD pricing is unavailable on a chain
      const rawTotal = (us.userReservesData || []).reduce((acc, r) => {
        const sup = Number(r.underlyingBalance || '0') || 0;
        const bor = Number(r.totalBorrows || '0') || 0;
        return acc + sup + bor;
      }, 0);
      return rawTotal > 0;
    })
    .map((net) => {
      const us = net?.userSummary;
      const filteredReserves = us?.userReservesData
        ? us.userReservesData.filter((r) => {
            const sup = Number(r.underlyingBalanceUSD || r.underlyingBalance || '0') || 0;
            const bor = Number(r.totalBorrowsUSD || r.totalBorrows || '0') || 0;
            return sup > 0 || bor > 0;
          })
        : [];
      return us ? { ...net, userSummary: { ...us, userReservesData: filteredReserves } } : net;
    });

  const filterReserves = (rows) =>
    (rows || []).filter((r) => {
      const supUSD = Number(r.underlyingBalanceUSD || '0') || 0;
      const borUSD = Number(r.totalBorrowsUSD || '0') || 0;
      const sup = Number(r.underlyingBalance || '0') || 0;
      const bor = Number(r.totalBorrows || '0') || 0;
      return supUSD > 0 || borUSD > 0 || sup > 0 || bor > 0;
    });

  const networkSupplyUSD = (net) => {
    const rows = filterReserves(net?.userSummary?.userReservesData);
    return rows.reduce((acc, r) => acc + (Number(r.underlyingBalanceUSD || r.underlyingBalance || '0') || 0), 0);
  };

  const computeWalletTotals = (nets) => {
    let totalSupplyUSD = 0;
    let totalBorrowUSD = 0;
    nets
      .filter((net) => {
        if (net?.error) return true;
        const us = net?.userSummary;
        if (!us) return false;
        const usdTotal = (Number(us.totalCollateralUSD || '0') || 0) + (Number(us.totalBorrowsUSD || '0') || 0);
        if (usdTotal > 0) return true;
        const rawTotal = (us.userReservesData || []).reduce((acc, r) => {
          const sup = Number(r.underlyingBalance || '0') || 0;
          const bor = Number(r.totalBorrows || '0') || 0;
          return acc + sup + bor;
        }, 0);
        return rawTotal > 0;
      })
      .forEach((net) => {
        const rows = filterReserves(net?.userSummary?.userReservesData);
        for (const r of rows) {
          totalSupplyUSD += Number(r.underlyingBalanceUSD || '0') || 0;
          totalBorrowUSD += Number(r.totalBorrowsUSD || '0') || 0;
        }
      });
    return { totalSupplyUSD, totalBorrowUSD };
  };

  const renderNetworks = (nets, keyPrefix = 'net') =>
    nets
      .filter((net) => {
        if (net?.error) return true;
        const us = net?.userSummary;
        if (!us) return false;
        const usdTotal = (Number(us.totalCollateralUSD || '0') || 0) + (Number(us.totalBorrowsUSD || '0') || 0);
        if (usdTotal > 0) return true;
        const rawTotal = (us.userReservesData || []).reduce((acc, r) => {
          const sup = Number(r.underlyingBalance || '0') || 0;
          const bor = Number(r.totalBorrows || '0') || 0;
          return acc + sup + bor;
        }, 0);
        return rawTotal > 0;
      })
      .sort((a, b) => networkSupplyUSD(b) - networkSupplyUSD(a))
      .map((net, idx) => (
      <div key={`${keyPrefix}-${net.networkKey}-${idx}`} className="border border-gray-100 dark:border-gray-800 rounded mb-3">
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="font-medium text-gray-800 dark:text-gray-200">{net.networkLabel}</div>
          {net.poolSummary && (
            <div className="text-xs text-gray-600 dark:text-gray-300 flex gap-3 items-center">
              <div>HF: <HealthBadge health={net.poolSummary.healthFactor} /></div>
              <div>LTV: {formatNumber(Number(net.poolSummary.ltv) / 100)}%</div>
              <div>Liq.Th: {formatNumber(Number(net.poolSummary.currentLiquidationThreshold) / 100)}%</div>
            </div>
          )}
        </div>
        <div className="p-3">
          {net.userSummary && filterReserves(net.userSummary.userReservesData).length > 0 ? (
            (() => {
              const rows = filterReserves(net.userSummary.userReservesData);
              const sumSupplyUSD = rows.reduce((acc, r) => acc + (Number(r.underlyingBalanceUSD || '0') || 0), 0);
              const sumBorrowUSD = rows.reduce((acc, r) => acc + (Number(r.totalBorrowsUSD || '0') || 0), 0);
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600 dark:text-gray-300">
                        <th className="px-2 py-1">Asset</th>
                        <th className="px-2 py-1 text-right">Supply</th>
                        <th className="px-2 py-1 text-right">Supply USD</th>
                        <th className="px-2 py-1 text-right">Borrow</th>
                        <th className="px-2 py-1 text-right">Borrow USD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.reserve.underlyingAsset} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-2 py-1 text-gray-800 dark:text-gray-200">{r.reserve.symbol}</td>
                          <td className="px-2 py-1 text-right">{formatNumber(r.underlyingBalance)}</td>
                          <td className="px-2 py-1 text-right">${formatUSD(r.underlyingBalanceUSD)}</td>
                          <td className="px-2 py-1 text-right">{formatNumber(r.totalBorrows)}</td>
                          <td className="px-2 py-1 text-right">${formatUSD(r.totalBorrowsUSD)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-200 dark:border-gray-700 font-medium">
                        <td className="px-2 py-1">Totals</td>
                        <td className="px-2 py-1 text-right">—</td>
                        <td className="px-2 py-1 text-right">${formatUSD(sumSupplyUSD)}</td>
                        <td className="px-2 py-1 text-right">—</td>
                        <td className="px-2 py-1 text-right">${formatUSD(sumBorrowUSD)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()
          ) : (
            <div className="text-sm text-gray-600 dark:text-gray-300">No positions found</div>
          )}
        </div>
      </div>
    ));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Lending Check</h1>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        Check lending and borrowing positions across Aave v3 and Venus on supported networks.
      </p>
      {/* Stored wallets controls */}
      <div className="mb-3">
        <form onSubmit={handleAddStored} className="flex gap-2">
          <input
            type="text"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            placeholder="Add and check wallet 0x..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none"
          >
            Add
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50"
          >
            {loading ? 'Checking…' : 'Check once'}
          </button>
        </form>
        {/* Stored wallets selector buttons */}
        {storedWallets.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {storedWallets.map((w) => {
              const isSelected = selectedWallet && selectedWallet.toLowerCase() === w.address.toLowerCase();
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setSelectedWallet((prev) => (prev && prev.toLowerCase() === w.address.toLowerCase() ? null : w.address))}
                  className={`px-2 py-1 text-xs rounded border ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700'}`}
                  title={w.address}
                >
                  {w.label ? w.label : `${w.address.slice(0, 6)}…${w.address.slice(-4)}`}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handleRefreshStored(true)}
              className="ml-auto px-3 py-1.5 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:opacity-50"
            >
              Refresh all
            </button>
          </div>
        )}
        {loading && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">Loading…</div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 mb-4 hidden">
        <input
          type="text"
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          placeholder="0x... wallet address"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Check'}
        </button>
      </form>
      {error && (
        <div className="mb-1 text-red-600 text-sm">{error}</div>
      )}
      {progressMsg && (
        <div className="mb-4 text-xs text-gray-600 dark:text-gray-300">{progressMsg}</div>
      )}

      <div className="space-y-6">
        {singleResult && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
              <div className="font-medium text-gray-800 dark:text-gray-200">
                Wallet <span className="font-mono text-ms ml-1">{singleResult.address.slice(0,6)}…{singleResult.address.slice(-4)}</span>
                <a
                  href={`https://debank.com/profile/${singleResult.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-3 text-xs text-blue-600 hover:underline"
                >
                  Open on DeBank
                </a>
              </div>
            </div>
            <div className="p-3">
              {singleResult.loading ? (
                <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
              ) : singleResult.error ? (
                <div className="text-sm text-red-600">{singleResult.error}</div>
              ) : (
                (() => {
                  const totals = computeWalletTotals(singleResult.results || []);
                  return (
                    <div>
                      <div className="text-xs text-gray-700 dark:text-gray-300 mb-2">
                        Totals: Supply ${formatUSD(totals.totalSupplyUSD)} · Borrow ${formatUSD(totals.totalBorrowUSD)}
                      </div>
                      {renderNetworks(singleResult.results, 'single-net')}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}

        {displayed.map((net, idx) => (
          <div key={`${net.walletAddress || 'single'}-${net.networkKey}-${idx}`} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
              <div className="font-medium text-gray-800 dark:text-gray-200">
                {net.networkLabel}
                {net.walletAddress && (
                  <span className="ml-2 text-xs text-gray-500">{net.walletLabel ? `${net.walletLabel} · ` : ''}{net.walletAddress.slice(0,6)}…{net.walletAddress.slice(-4)}</span>
                )}
              </div>
              {net.poolSummary && (
                <div className="text-xs text-gray-600 dark:text-gray-300 flex gap-3 items-center">
                  <div>
                    HF: <HealthBadge health={net.poolSummary.healthFactor} />
                  </div>
                  <div>LTV: {formatNumber(Number(net.poolSummary.ltv) / 100)}%</div>
                  <div>Liq.Th: {formatNumber(Number(net.poolSummary.currentLiquidationThreshold) / 100)}%</div>
                  {net.userSummary?.isInIsolationMode && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      Isolation{net.userSummary?.isolatedReserve?.symbol ? `: ${net.userSummary.isolatedReserve.symbol}` : ''}
                    </span>
                  )}
                </div>
              )}
            </div>

            {net.error ? (
              <div className="px-4 py-3 text-sm text-red-600">{net.error}</div>
            ) : (
              <div className="p-3">
                {net.userSummary && filterReserves(net.userSummary.userReservesData).length > 0 ? (
                  (() => {
                    const rows = filterReserves(net.userSummary.userReservesData);
                    const sumSupplyUSD = rows.reduce(
                      (acc, r) => acc + (Number(r.underlyingBalanceUSD || '0') || 0),
                      0
                    );
                    const sumBorrowUSD = rows.reduce(
                      (acc, r) => acc + (Number(r.totalBorrowsUSD || '0') || 0),
                      0
                    );
                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-600 dark:text-gray-300">
                              <th className="px-2 py-1">Asset</th>
                              <th className="px-2 py-1 text-right">Supply</th>
                              <th className="px-2 py-1 text-right">Supply USD</th>
                              <th className="px-2 py-1 text-right">Borrow</th>
                              <th className="px-2 py-1 text-right">Borrow USD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.reserve.underlyingAsset} className="border-t border-gray-100 dark:border-gray-800">
                                <td className="px-2 py-1 text-gray-800 dark:text-gray-200">{r.reserve.symbol}</td>
                                <td className="px-2 py-1 text-right">{formatNumber(r.underlyingBalance)}</td>
                                <td className="px-2 py-1 text-right">${formatUSD(r.underlyingBalanceUSD)}</td>
                                <td className="px-2 py-1 text-right">{formatNumber(r.totalBorrows)}</td>
                                <td className="px-2 py-1 text-right">${formatUSD(r.totalBorrowsUSD)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-gray-200 dark:border-gray-700 font-medium">
                              <td className="px-2 py-1">Totals</td>
                              <td className="px-2 py-1 text-right">—</td>
                              <td className="px-2 py-1 text-right">${formatUSD(sumSupplyUSD)}</td>
                              <td className="px-2 py-1 text-right">—</td>
                              <td className="px-2 py-1 text-right">${formatUSD(sumBorrowUSD)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-300">No positions found</div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Stored wallets sections */}
        {walletStates
          .filter((w) => !selectedWallet || w.address.toLowerCase() === selectedWallet.toLowerCase())
          .sort((a, b) => {
            if (selectedWallet) return 0;
            const ta = computeWalletTotals(a.results || []).totalSupplyUSD;
            const tb = computeWalletTotals(b.results || []).totalSupplyUSD;
            return tb - ta;
          })
          .map((w) => {
            const addressKey = w.address.toLowerCase();
            const displayable = (w.results || []).filter((net) => {
              if (net?.error) return true;
              const us = net?.userSummary;
              if (!us) return false;
              const usdTotal = (Number(us.totalCollateralUSD || '0') || 0) + (Number(us.totalBorrowsUSD || '0') || 0);
              if (usdTotal > 0) return true;
              const rawTotal = (us.userReservesData || []).reduce((acc, r) => {
                const sup = Number(r.underlyingBalance || '0') || 0;
                const bor = Number(r.totalBorrows || '0') || 0;
                return acc + sup + bor;
              }, 0);
              return rawTotal > 0;
            }).sort((a, b) => networkSupplyUSD(b) - networkSupplyUSD(a));
            const selectedId = selectedProtocols[addressKey] || null;
            const filteredNets = selectedId ? displayable.filter((n) => n.networkLabel === selectedId) : displayable;
            const totals = computeWalletTotals(filteredNets || []);
            return (
              <div key={`wallet-${w.address}`} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="px-4 mt-3 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
                  <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    {w.label ? `${w.label} · ` : ''}
                    <a
                      href={`https://debank.com/profile/${w.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm text-blue-600 hover:underline inline-flex items-center gap-2"
                      title="Open on DeBank"
                    >
                      <img src="https://assets.debank.com/favicon.ico" alt="DeBank" className="w-4 h-4" />
                      {w.address.slice(0,6)}…{w.address.slice(-4)}
                    </a>
                    <span className="text-xs text-gray-600 dark:text-gray-300">· Supply ${formatUSD(totals.totalSupplyUSD)} · Borrow ${formatUSD(totals.totalBorrowUSD)}</span>
                  </div>
                  <div className="text-xs flex items-center gap-3">
                    {w.updatedAt && (
                      <span className="text-gray-600 dark:text-gray-400">Updated {timeAgo(w.updatedAt)}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setRemoveModal({ address: w.address, label: w.label })}
                      className="px-2 py-0.5 text-xs rounded bg-red-600 text-white"
                      title="Remove wallet"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  {w.loading ? (
                    <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
                  ) : w.error ? (
                    <div className="text-sm text-red-600">{w.error}</div>
                  ) : (
                    <div>
                      {displayable.length > 1 && (
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {displayable.map((n, i) => {
                            const id = n.networkLabel;
                            const isSelected = !!selectedId && selectedId === id;
                            const hf = Number(n.poolSummary?.healthFactor || '0') / 1e18;
                            const badge = !isFinite(hf) || hf >= 10 ? '10+' : hf === 0 ? '—' : hf.toFixed(2);
                            const color = (!isFinite(hf) || hf >= 10) ? 'bg-green-600' : hf === 0 ? 'bg-gray-500' : hf < 1.05 ? 'bg-red-600' : hf < 1.5 ? 'bg-yellow-600' : 'bg-green-600';
                            return (
                              <button
                                key={`${addressKey}-proto-${i}`}
                                type="button"
                                onClick={() => setSelectedProtocols((prev) => ({
                                  ...prev,
                                  [addressKey]: isSelected ? null : id,
                                }))}
                                className={`px-2 py-1 text-xs rounded border flex items-center gap-2 ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-700'}`}
                              >
                                <span>{n.networkLabel}</span>
                                <span className={`inline-block px-1.5 py-0.5 rounded text-white ${color}`}>{badge}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {renderNetworks(filteredNets, `wallet-${w.address}`)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        {removeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setRemoveModal(null)} />
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 w-full max-w-sm shadow-lg">
              <div className="text-sm text-gray-800 dark:text-gray-100 mb-3">
                Remove wallet {removeModal.label ? `${removeModal.label} · ` : ''}
                <span className="font-mono">{removeModal.address.slice(0,6)}…{removeModal.address.slice(-4)}</span>?
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  onClick={() => setRemoveModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm rounded bg-red-600 text-white"
                  onClick={async () => {
                    await handleRemoveStored(removeModal.address);
                    setRemoveModal(null);
                    clearCacheEntry(removeModal.address);
                  }}
                >
                  Remove wallet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LendingCheckPage;


